import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ArrowUpDown, Funnel, Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  EditorialContainer,
  PageTitle,
  TopSpacer,
  usePublicationMeta,
} from '../components/editorial/PublicationShared';
import {
  formatInsightPublishedAt,
  INSIGHT_V2_PLACEHOLDER_IMAGE,
  listInsightV2Posts,
} from '../features/insights-v2/content';
import { insightsV2ServicesBackground } from '../features/insights-v2/styles';
import { getPublicationCategories } from '../features/publications/taxonomy';
import { usePublicPublications } from '../hooks/usePublications';

const POSTS_PER_PAGE = 6;

const Page = styled.main`
  min-height: 100vh;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  ${insightsV2ServicesBackground}
  color: #101625;

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const IntroWrap = styled.div`
  max-width: 56rem;
  margin: 0 auto;
  display: grid;
  justify-items: center;
  text-align: center;
`;

const HeroSection = styled.section`
  padding: 0 0 3.25rem;
  background: transparent;
  color: #1f2937;
`;

const BodySection = styled.section`
  background: transparent;
  padding: 0 0 5.5rem;
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 1.4rem;
`;

const HeroTitle = styled(PageTitle)`
  max-width: none;
  color: #1f2937;
  text-shadow: 0 10px 30px rgba(255, 255, 255, 0.22);
`;

const DesktopCategoryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  flex-wrap: wrap;
  padding: 0;
  max-width: 64rem;
  margin: 0 auto;

  @media (max-width: 760px) {
    display: none;
  }
`;

const DesktopCategoryButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.6rem;
  padding: 0.7rem 1rem;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.26)' : 'rgba(16, 22, 37, 0.1)')};
  background: ${({ $active }) =>
    $active
      ? 'rgba(255, 245, 232, 0.62)'
      : 'rgba(255, 255, 255, 0.42)'};
  backdrop-filter: blur(18px) saturate(135%);
  -webkit-backdrop-filter: blur(18px) saturate(135%);
  color: ${({ $active }) => ($active ? '#8c5b24' : 'rgba(16, 22, 37, 0.76)')};
  font: inherit;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease, color 160ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(224, 161, 94, 0.28);
    color: #8c5b24;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 0 2rem;
  flex-wrap: wrap;
`;

const SearchWrap = styled.div`
  flex: 1 1 22rem;
  min-width: min(100%, 18rem);
  position: relative;
`;

const SearchIconWrap = styled.span`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  color: rgba(16, 22, 37, 0.46);
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  border-radius: 16px;
  border: 1px solid rgba(16, 22, 37, 0.12);
  background: rgba(255, 255, 255, 0.42);
  backdrop-filter: blur(18px) saturate(135%);
  -webkit-backdrop-filter: blur(18px) saturate(135%);
  color: #101625;
  padding: 0.92rem 3rem 0.92rem 2.95rem;
  font-size: 0.96rem;
  line-height: 1;
  outline: none;
  transition: border-color 160ms ease, box-shadow 160ms ease;

  &::placeholder {
    color: rgba(16, 22, 37, 0.42);
  }

  &:focus {
    border-color: rgba(224, 161, 94, 0.35);
    box-shadow: 0 0 0 3px rgba(224, 161, 94, 0.12);
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: none;
  background: transparent;
  color: rgba(16, 22, 37, 0.54);
  cursor: pointer;

  &:hover {
    color: #101625;
    background: rgba(16, 22, 37, 0.06);
  }
`;

const ControlCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-wrap: wrap;
`;

const MenuWrap = styled.div`
  position: relative;
`;

const MobileOnlyMenuWrap = styled(MenuWrap)`
  @media (min-width: 761px) {
    display: none;
  }
`;

const IconButton = styled.button<{ $active?: boolean }>`
  width: 3rem;
  height: 3rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.32)' : 'rgba(16, 22, 37, 0.12)')};
  background: ${({ $active }) =>
    $active
      ? 'rgba(255, 245, 232, 0.62)'
      : 'rgba(255, 255, 255, 0.42)'};
  backdrop-filter: blur(18px) saturate(135%);
  -webkit-backdrop-filter: blur(18px) saturate(135%);
  color: ${({ $active }) => ($active ? '#8c5b24' : '#101625')};
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease, color 160ms ease, transform 160ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(224, 161, 94, 0.32);
    color: #8c5b24;
  }
`;

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 0.55rem);
  right: 0;
  min-width: 14rem;
  max-width: min(20rem, calc(100vw - 1.5rem));
  max-height: min(22rem, calc(100vh - 8rem));
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0.55rem;
  border-radius: 18px;
  border: 1px solid rgba(16, 22, 37, 0.12);
  background: rgba(255, 255, 255, 0.52);
  backdrop-filter: blur(22px) saturate(145%);
  -webkit-backdrop-filter: blur(22px) saturate(145%);
  box-shadow: 0 22px 48px rgba(16, 22, 37, 0.14);
  z-index: 10;
  display: grid;
  gap: 0.35rem;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(16, 22, 37, 0.16);
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: padding-box;
  }

  @media (max-width: 640px) {
    left: 0;
    right: auto;
    min-width: min(14rem, calc(100vw - 2rem));
    max-width: calc(100vw - 2rem);
  }
`;

const PopoverButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.24)' : 'transparent')};
  border-radius: 14px;
  padding: 0.78rem 0.85rem;
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.12)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#101625' : 'rgba(16, 22, 37, 0.82)')};
  text-align: left;
  cursor: pointer;
  font: inherit;
  font-size: 0.92rem;
  font-weight: 600;

  &:hover {
    background: rgba(16, 22, 37, 0.05);
    color: #101625;
  }
`;

const PopoverMeta = styled.span`
  color: rgba(16, 22, 37, 0.46);
  font-size: 0.77rem;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const EmptyState = styled.div`
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(180deg, rgba(9, 14, 24, 0.98), rgba(16, 24, 40, 0.96));
  padding: 2.2rem 1.6rem;
  color: rgba(237, 242, 251, 0.78);
  text-align: center;
`;

const BlogGrid = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.6rem;
  padding: 0;

  @media (max-width: 740px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCardLink = styled(Link)`
  display: block;
  color: inherit;
  text-decoration: none;
  perspective: 1400px;
`;

const BlogCard = styled.article<{ $glow: string }>`
  position: relative;
  height: 100%;
  min-height: 360px;
  height: 360px;
  display: grid;
  grid-template-columns: minmax(280px, 0.95fr) minmax(0, 1.25fr);
  border-radius: 28px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.52);
  backdrop-filter: blur(22px) saturate(145%);
  -webkit-backdrop-filter: blur(22px) saturate(145%);
  box-shadow:
    0 22px 48px rgba(16, 22, 37, 0.14),
    0 18px 42px ${({ $glow }) => $glow};
  transform-origin: center bottom;
  transition:
    transform 180ms ease,
    box-shadow 180ms ease,
    background 180ms ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(90deg, rgba(255, 255, 255, 0.22) 1px, transparent 1px),
      linear-gradient(rgba(255, 255, 255, 0.18) 1px, transparent 1px);
    background-size: 26px 26px;
    opacity: 0.18;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-8px) rotateX(2deg) rotateY(-2deg);
    background: rgba(255, 255, 255, 0.58);
    box-shadow:
      0 30px 60px rgba(16, 22, 37, 0.18),
      0 20px 50px ${({ $glow }) => $glow};
  }

  @media (max-width: 900px) {
    min-height: auto;
    height: auto;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }
`;

const BlogVisual = styled.div<{
  $background: string;
  $image: string;
  $imageFit: 'cover' | 'contain';
  $imagePosition: string;
  $imageScale: number;
}>`
  position: relative;
  min-height: 100%;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.42), rgba(255, 255, 255, 0.18)),
    ${({ $background }) => $background};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.18), transparent 28%),
      linear-gradient(90deg, rgba(255, 255, 255, 0.16) 1px, transparent 1px),
      linear-gradient(rgba(255, 255, 255, 0.14) 1px, transparent 1px);
    background-size: auto, 28px 28px, 28px 28px;
    opacity: 0.22;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url(${({ $image }) => $image});
    background-repeat: no-repeat;
    background-position: ${({ $imagePosition }) => $imagePosition};
    background-size: ${({ $imageFit, $imageScale }) =>
      $imageFit === 'cover'
        ? `${120 * $imageScale}% auto`
        : `auto ${82 * $imageScale}%`};
    filter: saturate(0.96) contrast(1.01);
    pointer-events: none;
  }

  @media (max-width: 900px) {
    aspect-ratio: 16 / 10;
    min-height: auto;
  }
`;

const BlogBody = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  gap: 1rem;
  align-content: start;
  padding: 1.45rem 1.45rem 1.5rem;
  color: #101625;
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const CategoryPill = styled.span`
  display: inline-block;
  padding: 0;
  color: #8c5b24;
  font-size: 0.72rem;
  line-height: 1;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 800;
`;

const CardDate = styled.span`
  color: rgba(16, 22, 37, 0.58);
  font-size: 0.84rem;
  line-height: 1;
  font-weight: 600;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-family: var(--font-serif);
  font-size: clamp(1.8rem, 2.4vw, 2.45rem);
  line-height: 0.96;
  letter-spacing: -0.045em;
  color: #101625;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardTeaser = styled.p`
  margin: 0;
  color: rgba(16, 22, 37, 0.76);
  font-size: 0.98rem;
  line-height: 1.68;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  flex-wrap: wrap;
  margin-top: auto;
  padding-top: 0.4rem;
  border-top: 1px solid rgba(16, 22, 37, 0.08);
  color: rgba(16, 22, 37, 0.62);
  font-size: 0.88rem;
  font-weight: 600;
`;

const AuthorMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
`;

const AuthorAvatar = styled.span<{ $src?: string }>`
  width: 1.9rem;
  height: 1.9rem;
  min-width: 1.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 999px;
  border: 1px solid rgba(16, 22, 37, 0.12);
  background: rgba(255, 255, 255, 0.48);
  color: #101625;
  font-size: 0.72rem;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const AuthorText = styled.span`
  min-width: 0;
  color: #101625;
  font-weight: 700;
`;

const ReadTime = styled.span`
  white-space: nowrap;
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2.25rem;
  padding-top: 1.4rem;
  border-top: 1px solid rgba(12, 19, 32, 0.1);
  flex-wrap: wrap;
`;

const PaginationMeta = styled.div`
  color: rgba(14, 22, 36, 0.7);
  font-size: 0.94rem;
  font-weight: 600;
`;

const PaginationActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
`;

const PageButton = styled.button`
  min-width: 2.8rem;
  height: 2.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  border-radius: 14px;
  border: 1px solid rgba(12, 19, 32, 0.14);
  background: rgba(255, 255, 255, 0.72);
  color: #0f1728;
  font: inherit;
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: rgba(224, 161, 94, 0.4);
    background: #fff;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const PageNumber = styled(PageButton)<{ $active?: boolean }>`
  background: ${({ $active }) => ($active ? '#0f1728' : 'rgba(255, 255, 255, 0.72)')};
  color: ${({ $active }) => ($active ? '#f7efe6' : '#0f1728')};
  border-color: ${({ $active }) => ($active ? '#0f1728' : 'rgba(12, 19, 32, 0.14)')};
`;

function getAuthorInitials(author: string) {
  return author
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function parsePublishedDate(value: string) {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

type SortMode = 'newest' | 'oldest' | 'title-asc' | 'title-desc';

export default function InsightsV2Page() {
  const publicPublications = usePublicPublications();

  usePublicationMeta(
    'CyBard Journal',
    'Explore the latest CyBard journal writing on strategy, commerce, branding, systems, and market change.',
  );

  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [page, setPage] = useState(1);
  const filterMenuRef = useRef<HTMLDivElement | null>(null);
  const sortMenuRef = useRef<HTMLDivElement | null>(null);

  const allPosts = useMemo(
    () => listInsightV2Posts(publicPublications.data),
    [publicPublications.data],
  );

  const categories = useMemo(
    () => {
      const availableCategories = getPublicationCategories(allPosts.map((post) => post.category)).filter((category) =>
        allPosts.some((post) => post.category.toLowerCase() === category.toLowerCase()),
      );
      return ['all', ...availableCategories];
    },
    [allPosts],
  );

  const filteredPosts = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const next = allPosts.filter((post) => {
      const matchesCategory =
        categoryFilter === 'all' || post.category.toLowerCase() === categoryFilter.toLowerCase();
      if (!matchesCategory) return false;
      if (!normalizedSearch) return true;
      const haystack = [post.title, post.subtitle, post.excerpt, post.author, post.category]
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedSearch);
    });

    next.sort((a, b) => {
      switch (sortMode) {
        case 'oldest':
          return parsePublishedDate(a.publishedAt) - parsePublishedDate(b.publishedAt);
        case 'title-asc':
          return a.title.localeCompare(b.title);
        case 'title-desc':
          return b.title.localeCompare(a.title);
        case 'newest':
        default:
          return parsePublishedDate(b.publishedAt) - parsePublishedDate(a.publishedAt);
      }
    });

    return next;
  }, [allPosts, categoryFilter, search, sortMode]);

  useEffect(() => {
    if (!filterOpen && !sortOpen) return undefined;

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (filterMenuRef.current?.contains(target) || sortMenuRef.current?.contains(target)) return;
      setFilterOpen(false);
      setSortOpen(false);
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setFilterOpen(false);
      setSortOpen(false);
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [filterOpen, sortOpen]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const paginatedPosts = useMemo(() => {
    const start = (page - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(start, start + POSTS_PER_PAGE);
  }, [filteredPosts, page]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <Page>
      <HeroSection>
        <TopSpacer />
        <EditorialContainer>
          <HeroInner>
            <IntroWrap>
              <HeroTitle>CyBard Journal</HeroTitle>
            </IntroWrap>
            <DesktopCategoryRow>
              {categories.filter((category) => category !== 'all').map((category) => {
                const isActive = categoryFilter === category;
                return (
                  <DesktopCategoryButton
                    key={category}
                    type="button"
                    $active={isActive}
                    onClick={() => {
                      setCategoryFilter(category);
                      setPage(1);
                    }}
                  >
                    {category}
                  </DesktopCategoryButton>
                );
              })}
            </DesktopCategoryRow>
          </HeroInner>
        </EditorialContainer>
      </HeroSection>

      <BodySection>
        <EditorialContainer style={{ position: 'relative', zIndex: 1 }}>
          <ControlsRow>
            <SearchWrap>
              <SearchIconWrap>
                <Search size={18} aria-hidden="true" />
              </SearchIconWrap>
              <SearchInput
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder="Search publications"
                aria-label="Search publications"
              />
              {search ? (
                <ClearButton
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setPage(1);
                  }}
                  aria-label="Clear search"
                >
                  <X size={16} aria-hidden="true" />
                </ClearButton>
              ) : null}
            </SearchWrap>

            <ControlCluster>
              <MobileOnlyMenuWrap ref={filterMenuRef}>
                <IconButton
                  type="button"
                  $active={categoryFilter !== 'all' || filterOpen}
                  aria-label="Filter publications"
                  onClick={() => {
                    setFilterOpen((current) => !current);
                    setSortOpen(false);
                  }}
                >
                  <Funnel size={18} aria-hidden="true" />
                </IconButton>
                {filterOpen ? (
                  <Popover>
                    {categories.map((category) => {
                      const isActive = categoryFilter === category;
                      return (
                        <PopoverButton
                          key={category}
                          type="button"
                          $active={isActive}
                          onClick={() => {
                            setCategoryFilter(category);
                            setPage(1);
                            setFilterOpen(false);
                          }}
                        >
                          <span>
                            {category === 'all' ? 'All categories' : category}
                          </span>
                          {isActive ? <PopoverMeta>Active</PopoverMeta> : null}
                        </PopoverButton>
                      );
                    })}
                  </Popover>
                ) : null}
              </MobileOnlyMenuWrap>

              <MenuWrap ref={sortMenuRef}>
                <IconButton
                  type="button"
                  $active={sortMode !== 'newest' || sortOpen}
                  aria-label="Sort publications"
                  onClick={() => {
                    setSortOpen((current) => !current);
                    setFilterOpen(false);
                  }}
                >
                  <ArrowUpDown size={18} aria-hidden="true" />
                </IconButton>
                {sortOpen ? (
                  <Popover>
                    {[
                      { key: 'newest', label: 'Newest first' },
                      { key: 'oldest', label: 'Oldest first' },
                      { key: 'title-asc', label: 'Title A-Z' },
                      { key: 'title-desc', label: 'Title Z-A' },
                    ].map((option) => {
                      const isActive = sortMode === option.key;
                      return (
                        <PopoverButton
                          key={option.key}
                          type="button"
                          $active={isActive}
                          onClick={() => {
                            setSortMode(option.key as SortMode);
                            setPage(1);
                            setSortOpen(false);
                          }}
                        >
                          <span>{option.label}</span>
                          {isActive ? <PopoverMeta>Active</PopoverMeta> : null}
                        </PopoverButton>
                      );
                    })}
                  </Popover>
                ) : null}
              </MenuWrap>
            </ControlCluster>
          </ControlsRow>

          <BlogGrid>
            {paginatedPosts.map((post) => {
              const visual = {
                image: post.coverImageUrl || INSIGHT_V2_PLACEHOLDER_IMAGE,
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.34), rgba(255, 255, 255, 0.14))',
                imageFit: 'cover' as const,
                imagePosition: 'center center',
                imageScale: 1,
              };

              return (
                <BlogCardLink key={post.slug} to={`/publications/${post.slug}`}>
                  <BlogCard $glow={post.theme.glow}>
                    <BlogVisual
                      $background={visual.background}
                      $image={visual.image}
                      $imageFit={visual.imageFit}
                      $imagePosition={visual.imagePosition}
                      $imageScale={visual.imageScale}
                    />
                    <BlogBody>
                      <CardTopRow>
                        <CategoryPill>{post.category}</CategoryPill>
                        <CardDate>{formatInsightPublishedAt(post.publishedAt)}</CardDate>
                      </CardTopRow>
                      <CardTitle>{post.title}</CardTitle>
                      <CardTeaser>{post.subtitle || post.excerpt}</CardTeaser>
                      <CardMetaRow>
                        <AuthorMeta>
                          <AuthorAvatar $src={post.authorAvatarUrl}>
                            {post.authorAvatarUrl ? (
                              <img src={post.authorAvatarUrl} alt={post.author} />
                            ) : (
                              getAuthorInitials(post.author)
                            )}
                          </AuthorAvatar>
                          <AuthorText>{post.author}</AuthorText>
                        </AuthorMeta>
                        <ReadTime>{post.readTime}</ReadTime>
                      </CardMetaRow>
                    </BlogBody>
                  </BlogCard>
                </BlogCardLink>
              );
            })}
            {!filteredPosts.length ? (
              <EmptyState>
                No insights match the current search and filter settings.
              </EmptyState>
            ) : null}
          </BlogGrid>

          {filteredPosts.length ? (
            <PaginationBar>
              <PaginationMeta>
                Showing {(page - 1) * POSTS_PER_PAGE + 1}-{Math.min(page * POSTS_PER_PAGE, filteredPosts.length)} of{' '}
                {filteredPosts.length} articles
              </PaginationMeta>
              {totalPages > 1 ? (
                <PaginationActions>
                  <PageButton type="button" onClick={() => setPage((current) => current - 1)} disabled={page === 1}>
                    <ArrowLeft size={16} aria-hidden="true" />
                  </PageButton>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                    <PageNumber
                      key={pageNumber}
                      type="button"
                      $active={pageNumber === page}
                      onClick={() => setPage(pageNumber)}
                      aria-label={`Go to page ${pageNumber}`}
                    >
                      {pageNumber}
                    </PageNumber>
                  ))}
                  <PageButton
                    type="button"
                    onClick={() => setPage((current) => current + 1)}
                    disabled={page === totalPages}
                  >
                    <ArrowRight size={16} aria-hidden="true" />
                  </PageButton>
                </PaginationActions>
              ) : null}
            </PaginationBar>
          ) : null}
        </EditorialContainer>
      </BodySection>
    </Page>
  );
}
